// src/pages/BatchesPage.jsx - Part 1: Setup and Functions
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, EyeIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, ClockIcon, PhotoIcon, TableCellsIcon, DocumentTextIcon, DocumentIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { batchAPI, adminAPI, downloadExcelFile, downloadFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';

const BatchesPage = () => {
  // EXISTING STATE
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [downloadingBatches, setDownloadingBatches] = useState(new Set());
  const [showDownloadMenus, setShowDownloadMenus] = useState(new Set());
  
  // NEW: Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10, // Good default for batches
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    startIndex: 0,
    endIndex: 0
  });
  
  const { user } = useAuth();
  const pollIntervalRef = useRef(null);

  // Portal-based dropdown menu for download actions
  function DropdownMenuPortal({ anchorRef, show, onClose, children }) {
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const menuRef = useRef(null);

    useEffect(() => {
      if (show && anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + window.scrollY + 4, // 4px margin
          left: rect.right - 160 + window.scrollX, // align right, width 160px
          width: 160,
        });
      }
    }, [show, anchorRef]);

    useEffect(() => {
      if (!show) return;
      const handleClick = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target) && !anchorRef.current.contains(e.target)) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, [show, onClose, anchorRef]);

    if (!show) return null;
    return createPortal(
      <div
        ref={menuRef}
        className="absolute z-50 bg-white rounded-md border border-gray-200 shadow-lg"
        style={{ top: position.top, left: position.left, width: position.width }}
      >
        <div className="py-1">{children}</div>
      </div>,
      document.body
    );
  }

  // Add a ref for each download button
  const downloadButtonRefs = useRef({});

  // FIXED: fetchBatches function - replace in your BatchesPage.jsx

  const fetchBatches = async (
    page = pagination.page, 
    search = searchTerm, 
    status = statusFilter, 
    sort = sortField, 
    order = sortDirection,
    customLimit = null  // NEW: Optional custom limit parameter
  ) => {
    try {
      const limitToUse = customLimit || pagination.limit; // Use custom limit if provided
      
      const queryParams = {
        page: page.toString(),
        limit: limitToUse.toString(), // Use the determined limit
        sort,
        order
      };

      if (search && search.trim()) {
        queryParams.search = search.trim();
      }
      
      if (status && status.trim()) {
        queryParams.status = status.trim();
      }

      const response = await batchAPI.list(queryParams);
      
      setBatches(response.data.data.batches);
      setPagination(response.data.data.pagination);
    } catch (error) {
      toast.error('Failed to load batches');
      console.error('Fetch batches error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExport = async (batch, format = 'excel') => {
    try {
      // Add to downloading set for loading state
      setDownloadingBatches(prev => new Set([...prev, batch.id]));
      
      // Show loading toast
      const formatName = format.toUpperCase();
      toast.loading(`Preparing ${formatName} file...`, { id: `download-${batch.id}` });
      
      // Call the enhanced download API
      const { blob, filename } = await batchAPI.downloadExport(batch.id, format);
      
      // Generate fallback filename if not provided by server
      const timestamp = new Date().toISOString().split('T')[0];
      const safeFileName = batch.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      
      let extension;
      switch (format.toLowerCase()) {
        case 'pdf':
          extension = 'pdf';
          break;
        case 'word':
        case 'docx':
          extension = 'docx';
          break;
        case 'csv':
          extension = 'csv';
          break;
        default:
          extension = 'xlsx';
      }
      
      const fallbackFilename = `${safeFileName}_${timestamp}_batch${batch.id}.${extension}`;
      
      // Download the file
      const success = downloadFile(blob, filename || fallbackFilename);
      
      if (success !== false) {
        toast.success(`${formatName} file downloaded successfully!`, { 
          id: `download-${batch.id}`,
          duration: 4000 
        });
      } else {
        throw new Error('Download failed - unable to save file');
      }
      
    } catch (error) {
      console.error(`${format} download error:`, error);
      
      let errorMessage = `Failed to download ${format.toUpperCase()} file`;
      
      if (error.response?.status === 400) {
        errorMessage = 'Batch processing is not completed yet';
      } else if (error.response?.status === 404) {
        errorMessage = 'Batch not found or access denied';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, { 
        id: `download-${batch.id}`,
        duration: 5000 
      });
    } finally {
      // Remove from downloading set
      setDownloadingBatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(batch.id);
        return newSet;
      });
    }
  };

  // Backward compatibility
  const handleDownloadExcel = (batch) => handleDownloadExport(batch, 'excel');

  // ALSO FIX: fetchBatchesQuietly function
  const fetchBatchesQuietly = async () => {
    try {
      // Build query parameters object
      const queryParams = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sort: sortField,
        order: sortDirection
      };

      // Only add search and status if they have values
      if (searchTerm && searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }
      
      if (statusFilter && statusFilter.trim()) {
        queryParams.status = statusFilter.trim();
      }

      const response = await batchAPI.list(queryParams);
      const newBatches = response.data.data.batches;
      
      setBatches(prevBatches => {
        const hasChanged = JSON.stringify(prevBatches) !== JSON.stringify(newBatches);
        return hasChanged ? newBatches : prevBatches;
      });
      
    } catch (error) {
      if (error.response?.status === 429) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setTimeout(() => {
          const hasActiveBatches = batches.some(batch => 
            batch.status === 'processing' || batch.status === 'queued'
          );
          if (hasActiveBatches) {
            pollIntervalRef.current = setInterval(fetchBatchesQuietly, 15000);
          }
        }, 30000);
        return;
      }
      console.error('Quiet fetch batches error:', error);
    }
  };

  // Initial fetch and setup (runs once on mount)
  useEffect(() => {
    fetchBatches(1); // Always start from page 1 when component mounts
  }, []); 

  // NEW: Handle search/filter/sort changes - reset to page 1
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchBatches(1, searchTerm, statusFilter, sortField, sortDirection);
    } else {
      fetchBatches(1, searchTerm, statusFilter, sortField, sortDirection);
    }
  }, [searchTerm, statusFilter, sortField, sortDirection]);

  // Polling management (runs when batches change)
  useEffect(() => {
    const hasActiveBatches = batches.some(batch => 
      batch.status === 'processing' || batch.status === 'queued'
    );

    if (hasActiveBatches && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(fetchBatchesQuietly, 15000);
    } else if (!hasActiveBatches && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Cleanup function
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [batches]);

  // Close download menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowDownloadMenus(new Set());
      }
    };

    if (showDownloadMenus.size > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenus]);

  // EXISTING HELPER FUNCTIONS
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate processing progress - IMPROVED VERSION for mixed results
  const getProgressInfo = (batch) => {
    const total = batch.total_images || 0;
    const processed = batch.processed_images || 0;
    const failed = batch.failed_images || 0;
    const success = batch.successful_images || 0;
    const processing = batch.processing_images || 0;
    
    if (batch.status === 'completed') {
      // For completed batches, check if there are failures
      const actualSuccess = success > 0 ? success : (total - failed);
      const actualFailed = failed || 0;
      
      if (actualFailed > 0) {
        // Mixed results: some success, some failed
        return {
          type: 'completed-mixed',
          percentage: total > 0 ? Math.round((actualSuccess / total) * 100) : 0,
          text: `${actualSuccess} completed, ${actualFailed} failed • Excel ready`,
          color: 'bg-yellow-500' // Yellow to indicate mixed results
        };
      } else {
        // All successful
        return {
          type: 'completed',
          percentage: 100,
          text: `${actualSuccess}/${total} completed • Excel ready`,
          color: 'bg-green-500'
        };
      }
    }else if (batch.status === 'processing') {
      const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;
      return {
        type: 'processing',
        percentage,
        text: `${processed}/${total} processed`,
        color: 'bg-blue-500'
      };
    } else if (batch.status === 'queued') {
      const queuePosition = batch.queue_position || 0;
      const estimatedWait = batch.estimated_wait_minutes || null;
      return {
        type: 'queued',
        percentage: 0,
        text: queuePosition > 0 ? `Position ${queuePosition} in queue` : 'Queued for processing',
        estimatedTime: estimatedWait,
        color: 'bg-yellow-500'
      };
    } else if (batch.status === 'failed') {
      // Check if it's completely failed or mixed
      if (success > 0) {
        // Some succeeded before batch failed
        return {
          type: 'failed-partial',
          percentage: total > 0 ? Math.round((success / total) * 100) : 0,
          text: `${success} completed, batch failed`,
          color: 'bg-orange-500'
        };
      } else {
        // Complete failure
        return {
          type: 'failed',
          percentage: 0,
          text: `Failed (${failed || total}/${total})`,
          color: 'bg-red-500'
        };
      }
    } else {
      return {
        type: 'pending',
        percentage: 0,
        text: total > 0 ? `${total} image${total > 1 ? 's' : ''} uploaded` : 'No images uploaded',
        color: 'bg-gray-500'
      };
    }
  };

  // Format estimated time
  const formatEstimatedTime = (estimatedMinutes) => {
    if (!estimatedMinutes || estimatedMinutes <= 0) return null;
    
    if (estimatedMinutes < 60) {
      return `~${Math.ceil(estimatedMinutes)}m`;
    } else {
      const hours = Math.floor(estimatedMinutes / 60);
      const minutes = Math.ceil(estimatedMinutes % 60);
      return minutes > 0 ? `~${hours}h ${minutes}m` : `~${hours}h`;
    }
  };

  // UPDATED: handleSort function
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    setSortField(field);
    setSortDirection(newDirection);
    // fetchBatches will be called by the useEffect above
  };

  // NEW: Pagination handlers
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchBatches(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit) => {
    
    setPagination(prev => ({ 
      ...prev, 
      limit: newLimit, 
      page: 1 
    }));
    
    // Pass the newLimit as the 6th parameter
    fetchBatches(1, searchTerm, statusFilter, sortField, sortDirection, newLimit);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Progress Bar Component
  const ProgressBar = ({ batch }) => {
    const progress = getProgressInfo(batch);
    
    return (
      <div className="w-full">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs font-medium text-gray-700">{progress.text}</span>
          {progress.estimatedTime && (
            <span className="flex items-center text-xs text-gray-500">
              <ClockIcon className="mr-1 w-3 h-3" />
              {formatEstimatedTime(progress.estimatedTime)}
            </span>
          )}
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${progress.color}`}
            style={{ width: `${progress.percentage}%` }}
          ></div>
        </div>
        {batch.status === 'processing' && progress.percentage > 0 && (
          <div className="mt-1 text-xs text-gray-500">
            {progress.percentage}% complete
          </div>
        )}
      </div>
    );
  };

  // NEW: Pagination Component
  const PaginationControls = () => {
  // Show pagination controls if there's more than one page OR if we want to show page size selector
  const showPageNumbers = pagination.totalPages > 1;
  const showPageSizeSelector = pagination.total > 5; // Show selector if more than 5 items total

  // Don't show anything if very few items
  if (pagination.total <= 5) return null;

  // FIXED: getPageNumbers function (this was missing!)
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, pagination.page - delta);
      i <= Math.min(pagination.totalPages - 1, pagination.page + delta);
      i++
    ) {
      range.push(i);
    }

    if (pagination.page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (pagination.page + delta < pagination.totalPages - 1) {
      rangeWithDots.push('...', pagination.totalPages);
    } else {
      rangeWithDots.push(pagination.totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="card">
      <div className="pt-6 card-content">
        <div className="flex flex-col gap-4 justify-between items-center sm:flex-row">
          {/* Results info */}
          <div className="text-sm text-gray-700">
            Showing {pagination.startIndex} to {pagination.endIndex} of {pagination.total} batches
          </div>

          {/* Page controls - only show if multiple pages */}
          {showPageNumbers ? (
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrev}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex items-center space-x-1">
                {getPageNumbers().map((pageNum, index) => (
                  <button
                    key={index}
                    onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                    disabled={pageNum === '...'}
                    className={`px-3 py-2 text-sm border rounded-md ${
                      pageNum === pagination.page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : pageNum === '...'
                        ? 'border-transparent cursor-default'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              {/* Next button */}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          ) : (
            // Empty div for spacing when no page numbers but we have page size selector
            showPageSizeSelector && <div></div>
          )}

          {/* Page size selector - always show if we have enough items */}
          {showPageSizeSelector && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Per page:</span>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  handleLimitChange(parseInt(e.target.value));
                }}
                className="px-2 py-1 text-sm bg-white rounded-md border border-gray-300 appearance-none"
                style={{ 
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="w-8 h-8 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
            <PhotoIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Batches</h1>
            <p className="text-gray-600">Manage your image processing batches</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center btn-primary"
        >
          <PlusIcon className="mr-2 w-5 h-5" />
          Create Batch
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="pt-6 card-content">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search batches by name, language, or comments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 input"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="queued">Queued</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* UPDATED: Batches Table with Pagination */}
      {batches.length === 0 && pagination.total === 0 ? (
        <div className="card">
          <div className="py-12 pt-6 text-center card-content">
            <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-gray-100 rounded-lg">
              <PlusIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">No batches yet</h3>
            <p className="mb-4 text-gray-500">Get started by creating your first batch</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First Batch
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="pt-6 card-content">
              {batches.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-500">No batches match your search criteria</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 font-medium text-left text-gray-900">
                          <button
                            onClick={() => handleSort('name')}
                            className="flex items-center space-x-1 hover:text-blue-600"
                          >
                            <span>Name</span>
                            {getSortIcon('name')}
                          </button>
                        </th>
                        <th className="px-4 py-3 font-medium text-left text-gray-900">
                          <button
                            onClick={() => handleSort('status')}
                            className="flex items-center space-x-1 hover:text-blue-600"
                          >
                            <span>Status</span>
                            {getSortIcon('status')}
                          </button>
                        </th>
                        <th className="px-4 py-3 font-medium text-left text-gray-900">
                          <button
                            onClick={() => handleSort('total_images')}
                            className="flex items-center space-x-1 hover:text-blue-600"
                          >
                            <span>Images</span>
                            {getSortIcon('total_images')}
                          </button>
                        </th>
                        <th className="px-4 py-3 w-64 font-medium text-left text-gray-900">
                          Progress
                        </th>
                        <th className="px-4 py-3 font-medium text-left text-gray-900">
                          <button
                            onClick={() => handleSort('language')}
                            className="flex items-center space-x-1 hover:text-blue-600"
                          >
                            <span>Language</span>
                            {getSortIcon('language')}
                          </button>
                        </th>
                        <th className="px-4 py-3 font-medium text-left text-gray-900">
                          <button
                            onClick={() => handleSort('created_at')}
                            className="flex items-center space-x-1 hover:text-blue-600"
                          >
                            <span>Created</span>
                            {getSortIcon('created_at')}
                          </button>
                        </th>
                        {user?.role === 'admin' && (
                          <th className="px-4 py-3 font-medium text-left text-gray-900">
                            <button
                              onClick={() => handleSort('total_cost')}
                              className="flex items-center space-x-1 hover:text-blue-600"
                            >
                              <span>Cost</span>
                              {getSortIcon('total_cost')}
                            </button>
                          </th>
                        )}
                        <th className="px-4 py-3 font-medium text-right text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((batch) => (
                        <tr key={batch.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{batch.name}</div>
                              {batch.comments && (
                                <div className="mt-1 max-w-xs text-sm text-gray-500 truncate">
                                  {batch.comments}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(batch.status)}`}>
                              {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-gray-900">
                            {batch.total_images || 0}
                          </td>
                          <td className="px-4 py-4">
                            <ProgressBar batch={batch} />
                          </td>
                          <td className="px-4 py-4 text-gray-900">
                            {batch.language}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {formatDate(batch.created_at)}
                          </td>
                          {user?.role === 'admin' && (
                            <td className="px-4 py-4 text-gray-900">
                              {batch.total_cost ? `$${parseFloat(batch.total_cost).toFixed(4)}` : '-'}
                            </td>
                          )}
                          <td className="px-4 py-4">
                            <div className="flex justify-end items-center space-x-2">
                              <button
                                onClick={() => navigate(`/batches/${batch.id}`)}
                                className="flex items-center btn-outline btn-sm"
                                title="View Details"
                              >
                                <EyeIcon className="mr-1 w-4 h-4" />
                                View
                              </button>
                              {batch.status === 'completed' && (
                                <div className="relative">
                                  {downloadingBatches.has(batch.id) ? (
                                    <button
                                      disabled
                                      className="flex items-center btn-outline btn-sm !bg-blue-50 !text-blue-700 !border-blue-200 opacity-75 cursor-not-allowed"
                                      title="Preparing download..."
                                    >
                                      <div className="mr-2 w-4 h-4 animate-spin">
                                        <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                                          <circle 
                                            className="opacity-25" 
                                            cx="12" 
                                            cy="12" 
                                            r="10" 
                                            stroke="currentColor" 
                                            strokeWidth="4"
                                          />
                                          <path 
                                            className="opacity-75" 
                                            fill="currentColor" 
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                          />
                                        </svg>
                                      </div>
                                      <span>Preparing...</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleDownloadExport(batch, 'word')}
                                      className="flex items-center btn-outline btn-sm !bg-blue-50 !text-blue-700 !border-blue-200 hover:!bg-blue-100 hover:!border-blue-300"
                                      title="Download Word document"
                                    >
                                      <DocumentIcon className="mr-2 w-4 h-4 text-blue-600" />
                                      <span>Download</span>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          
          {/* NEW: Add pagination controls */}
          <PaginationControls />
        </>
      )}

      {/* Create Batch Modal */}
      {showCreateModal && (
        <CreateBatchModal
          onClose={() => setShowCreateModal(false)}
          onBatchCreated={(newBatch) => {
            setBatches(prev => [newBatch, ...prev]);
            setShowCreateModal(false);
            setTimeout(() => {
              fetchBatchesQuietly();
            }, 2000);
          }}
        />
      )}
    </div>
  );
};

// Create Batch Modal Component
const CreateBatchModal = ({ onClose, onBatchCreated }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    language: 'English (United States)', // Updated to match database
    alt_text_length: 'medium',
    comments: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  
  // Enhanced upload progress tracking
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileStatuses, setFileStatuses] = useState({}); // Track status of each file
  const [uploadPhase, setUploadPhase] = useState(''); // 'uploading', 'validating', 'processing'
  const [detailedProgress, setDetailedProgress] = useState({
    uploaded: 0,
    validated: 0,
    failed: 0,
    total: 0
  });
  
  // Refs to track intervals for cleanup
  const intervalsRef = useRef([]);

  useEffect(() => {
    setIsVisible(true);
    fetchLanguages();
    
    // Cleanup intervals on unmount
    return () => {
      intervalsRef.current.forEach(interval => clearInterval(interval));
    };
  }, []);

  // Initialize file statuses when files are selected
  useEffect(() => {
    const initialStatuses = {};
    selectedFiles.forEach((file, index) => {
      initialStatuses[`${file.name}-${index}`] = {
        name: file.name,
        size: file.size,
        status: 'pending', // pending, uploading, validating, completed, failed
        progress: 0,
        error: null
      };
    });
    setFileStatuses(initialStatuses);
  }, [selectedFiles]);

  const fetchLanguages = async () => {
    try {
      const response = await batchAPI.getLanguages();
      if (response.data.error === false) {
        const activeLanguages = response.data.data.languages.filter(lang => lang.is_active);
        setLanguages(activeLanguages);
        if (activeLanguages.length > 0 && !activeLanguages.find(lang => lang.language_name === 'English (United States)')) {
          setFormData(prev => ({ ...prev, language: activeLanguages[0].language_name }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      setLanguages([
        { language_name: 'English (United States)', is_active: true },
        { language_name: 'German', is_active: true }
      ]);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const handleClose = () => {
    // Clean up any running intervals
    intervalsRef.current.forEach(interval => clearInterval(interval));
    intervalsRef.current = [];
    
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleNextStep = async (e) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
    } else {
      await handleCreateBatch();
    }
  };

  // Simplified and accurate upload progress callback
  const handleUploadProgress = (progressEvent) => {
    const rawProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    
    // Use actual progress from axios, but cap at 90% to show server processing phase
    const actualProgress = Math.min(rawProgress, 90);
    setUploadProgress(actualProgress);
    
    // Update all file statuses together (since they upload as a batch)
    setFileStatuses(prev => {
      const updated = { ...prev };
      
      Object.keys(updated).forEach(key => {
        if (actualProgress < 90) {
          // All files are uploading together
          updated[key] = { 
            ...updated[key], 
            status: 'uploading', 
            progress: actualProgress 
          };
        } else {
          // Upload complete, now validating on server
          updated[key] = { 
            ...updated[key], 
            status: 'validating', 
            progress: 100 
          };
        }
      });
      
      return updated;
    });
  };

  const handleCreateBatch = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setCreating(true);
    let createdBatchId = null;

    try {
      // Reset progress tracking
      setUploadProgress(0);
      setUploadPhase('uploading');
      setDetailedProgress({ uploaded: 0, validated: 0, failed: 0, total: selectedFiles.length });
      
      // Start with a small initial progress to show immediate feedback
      let initialProgress = 0;
      const initialInterval = setInterval(() => {
        initialProgress += 1;
        if (initialProgress <= 5) {
          setUploadProgress(initialProgress);
        } else {
          clearInterval(initialInterval);
          intervalsRef.current = intervalsRef.current.filter(id => id !== initialInterval);
        }
      }, 50);
      
      intervalsRef.current.push(initialInterval);

      const batchResponse = await batchAPI.create(formData);
      const batch = batchResponse.data.data.batch;
      createdBatchId = batch.id;

      setUploading(true);
      setUploadPhase('uploading');

      const uploadResponse = await batchAPI.upload(batch.id, selectedFiles, handleUploadProgress);

      // Smooth transition to validation phase
      setUploadPhase('validating');
      
      // Server-side processing phase (90% -> 95%)
      if (uploadProgress < 95) {
        setUploadProgress(95);
      }
      
              // Gradually update file statuses based on upload response
        if (uploadResponse.data.data.uploaded) {
          const uploadedFiles = uploadResponse.data.data.uploaded;
          const uploadErrors = uploadResponse.data.data.errors || [];
          
          // Animate file status updates gradually
          const updateFileStatusesGradually = () => {
            let index = 0;
            const interval = setInterval(() => {
              setFileStatuses(prev => {
                const updated = { ...prev };
                const keys = Object.keys(updated);
                
                if (index < keys.length) {
                  const key = keys[index];
                  const status = updated[key];
                  
                  // Check if this file was successfully uploaded
                  const matchingUpload = uploadedFiles.find(file => file.filename === status.name);
                  const matchingError = uploadErrors.find(error => error.filename === status.name);
                  
                  if (matchingUpload) {
                    updated[key] = {
                      ...status,
                      status: 'completed',
                      progress: 100
                    };
                  } else if (matchingError) {
                    updated[key] = {
                      ...status,
                      status: 'failed',
                      progress: 0,
                      error: matchingError.error
                    };
                  } else {
                    // File was uploaded but might be processing
                    updated[key] = {
                      ...status,
                      status: 'completed',
                      progress: 100
                    };
                  }
                  
                  index++;
                } else {
                  clearInterval(interval);
                }
                
                return updated;
              });
            }, 200);
          };
          
          // Start the gradual update after a brief delay
          setTimeout(updateFileStatusesGradually, 300);
          
          setDetailedProgress({
            uploaded: uploadedFiles.length,
            validated: uploadedFiles.length,
            failed: uploadErrors.length,
            total: selectedFiles.length
          });
        }

      const updatedTotalImages = uploadResponse.data.data.total_images;
      
      // Check if any images were successfully uploaded
      if (!updatedTotalImages || updatedTotalImages === 0) {
        // Clean up the empty batch
        await batchAPI.delete(batch.id);
        toast.error('No valid images were uploaded. Please check your files and try again.');
        return;
      }
      
      setUploadPhase('processing');
      
      // Complete the progress (95% -> 100%)
      setUploadProgress(100);
      
      const updatedBatch = {
        ...batch,
        total_images: updatedTotalImages,
        successful_images: 0,
        failed_images: 0,
        processed_images: 0,
        processing_images: 0,
        queued_images: 0,
        pending_images: updatedTotalImages
      };

      await batchAPI.process(batch.id);
      updatedBatch.status = 'queued';

      // Create success message based on upload response
      let successMessage = `Batch created with ${updatedTotalImages} images and added to processing queue!`;
      
      // Check if there's zip extraction summary in the response
      if (uploadResponse.data.data.zipSummary && uploadResponse.data.data.zipSummary.length > 0) {
        const zipSummary = uploadResponse.data.data.zipSummary;
        const totalExtracted = zipSummary.reduce((sum, zip) => sum + zip.extractedImages, 0);
        const totalSkipped = zipSummary.reduce((sum, zip) => sum + zip.skippedFiles, 0);
        
        successMessage = `Batch created with ${updatedTotalImages} images (${totalExtracted} extracted from ${zipSummary.length} zip file${zipSummary.length > 1 ? 's' : ''}) and added to processing queue!`;
        
        if (totalSkipped > 0) {
          successMessage += ` Note: ${totalSkipped} non-image files were skipped.`;
        }
      }

      toast.success(successMessage, { duration: 5000 });

      onBatchCreated(updatedBatch);

    } catch (error) {
      console.error('Create batch error:', error);
      
      // If we created a batch but something failed, clean it up
      if (createdBatchId) {
        try {
          await batchAPI.delete(createdBatchId);
          console.log('Cleaned up empty batch:', createdBatchId);
        } catch (deleteError) {
          console.error('Failed to clean up empty batch:', deleteError);
        }
      }
      
      // Update file statuses on error
      setFileStatuses(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (updated[key].status === 'uploading' || updated[key].status === 'pending') {
            updated[key] = { ...updated[key], status: 'failed', error: 'Upload failed' };
          }
        });
        return updated;
      });
      
      // Handle specific error cases
      if (error.response?.status === 400 && error.response?.data?.message === 'No valid images found') {
        toast.error('No valid images found in your selection. Please check your files and try again.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create batch or upload images');
      }
    } finally {
      setCreating(false);
      setUploading(false);
      setUploadPhase('');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const maxSize = 100 * 1024 * 1024; // 100MB limit
    
    // Expanded list of supported image types
    const validTypes = [
      // Common formats
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'image/bmp', 'image/tiff', 'image/tif', 'image/svg+xml',
      
      // Modern formats
      'image/avif', 'image/heic', 'image/heif',
      
      // HEIC variations (different systems report different MIME types)
      'image/x-heic', 'image/x-heif',
      
      // Vector/PostScript formats
      'application/postscript', 'application/eps', 'image/eps', 'image/x-eps',
      
      // AI (Adobe Illustrator) formats
      'application/illustrator', 'image/x-ai', 'application/vnd.adobe.illustrator',
      
      // PDF formats (for image extraction)
      'application/pdf',
      
      // Additional formats
      'image/x-icon', 'image/vnd.microsoft.icon', 'image/x-ms-bmp',
      'image/x-portable-pixmap', 'image/x-portable-graymap', 'image/x-portable-bitmap',
      'image/x-targa', 'image/x-rgb', 'image/jp2', 'image/jpx', 'image/jpm', 'image/mj2',
      
      // Zip files
      'application/zip', 'application/x-zip-compressed'
    ];
    
    const validFiles = [];
    const errors = [];
    
    files.forEach(file => {
      console.log(`File: ${file.name}, Type: ${file.type}, Size: ${Math.round(file.size / 1024 / 1024 * 100) / 100}MB`);
      
      // Get file extension for fallback detection
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const imageExtensions = [
        'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg',
        'avif', 'heic', 'heif', 'ico', 'ppm', 'pgm', 'pbm', 'tga', 'rgb',
        'jp2', 'jpx', 'jpm', 'mj2', 'eps', 'ai', 'pdf'
      ];
      
      // Check if it's a zip file
      const isZipFile = validTypes.includes(file.type) && 
                       (file.type === 'application/zip' || file.type === 'application/x-zip-compressed') ||
                       fileExtension === 'zip';
      
      // Check if it's a PDF file
      const isPdfFile = file.type === 'application/pdf' || fileExtension === 'pdf';
      
      // Check MIME type first, then fall back to file extension
      const isValidImage = validTypes.includes(file.type) || 
                          file.type.startsWith('image/') || 
                          imageExtensions.includes(fileExtension);
      
      if (!isValidImage && !isZipFile && !isPdfFile) {
        errors.push(`${file.name}: Invalid file type "${file.type}" (extension: .${fileExtension}) - not a supported image, PDF, or zip format`);
        return;
      }
      
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large (${Math.round(file.size / 1024 / 1024 * 100) / 100}MB, max 100MB)`);
        return;
      }
      
      validFiles.push(file);
    });

    if (errors.length > 0) {
      toast.error(`${errors.length} file(s) were skipped:\n${errors.join('\n')}`);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} file(s) added successfully`);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`fixed top-0 left-0 right-0 bottom-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ 
        zIndex: 9999,
        margin: 0,
        position: 'fixed'
      }}
      onClick={handleBackdropClick}
    >
      <div className={`bg-white rounded-lg shadow-xl max-w-2xl w-full transform transition-all duration-300 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {step === 1 ? 'Create New Batch' : 'Upload Images'}
            </h3>
            <div className="flex space-x-2">
              <div className={`w-8 h-2 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-2 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            </div>
          </div>
          
          <form onSubmit={handleNextStep}>
            {step === 1 ? (
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Batch Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="input"
                    placeholder="Enter batch name"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Language
                  </label>
                  {loadingLanguages ? (
                    <div className="flex justify-center items-center h-10 rounded-md border border-gray-300">
                      <div className="w-4 h-4 loading-spinner"></div>
                    </div>
                  ) : (
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      className="input"
                    >
                      {languages.map((lang) => (
                        <option key={lang.language_name} value={lang.language_name}>
                          {lang.language_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Alt-text Length
                  </label>
                  <select
                    name="alt_text_length"
                    value={formData.alt_text_length}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="short">Short (less than 15 words)</option>
                    <option value="medium">Medium (15-25 words)</option>
                    <option value="long">Long (more than 25 words)</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Comments (Optional)
                  </label>
                  <textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    rows={3}
                    className="input"
                    placeholder="Any special instructions..."
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Upload Images</h3>
                  <span className="text-sm text-gray-500">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                  </span>
                </div>

                {/* Upload Progress Section */}
                {(uploading || creating) && (
                  <div className="p-4 space-y-4 bg-gray-50 rounded-lg">
                    {/* Phase Indicator */}
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 loading-spinner"></div>
                        <span className="text-sm font-medium text-gray-700">
                          {uploadPhase === 'uploading' && 'Uploading files...'}
                          {uploadPhase === 'validating' && 'Validating images...'}
                          {uploadPhase === 'processing' && 'Adding to queue...'}
                          {!uploadPhase && creating && 'Creating batch...'}
                        </span>
                      </div>
                    </div>

                    {/* Overall Progress Bar */}
                    {uploading && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Overall Progress</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-blue-600 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Upload Statistics */}
                    {uploadPhase === 'validating' && (
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div className="p-2 bg-white rounded border">
                          <div className="text-lg font-semibold text-gray-900">{detailedProgress.total}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <div className="text-lg font-semibold text-green-600">{detailedProgress.uploaded}</div>
                          <div className="text-xs text-gray-500">Uploaded</div>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <div className="text-lg font-semibold text-blue-600">{detailedProgress.validated}</div>
                          <div className="text-xs text-gray-500">Validated</div>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <div className="text-lg font-semibold text-red-600">{detailedProgress.failed}</div>
                          <div className="text-xs text-gray-500">Failed</div>
                        </div>
                      </div>
                    )}

                    {/* Individual File Progress */}
                    {uploading && Object.keys(fileStatuses).length > 0 && (
                      <div className="overflow-y-auto space-y-2 max-h-60">
                        <h4 className="text-sm font-medium text-gray-700">File Progress</h4>
                        {Object.entries(fileStatuses).map(([key, status]) => (
                          <div key={key} className="flex items-center p-2 space-x-3 bg-white rounded border">
                            {/* Status Icon */}
                            <div className="flex-shrink-0">
                              {status.status === 'pending' && (
                                <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                              )}
                              {status.status === 'uploading' && (
                                <div className="w-4 h-4 loading-spinner"></div>
                              )}
                              {status.status === 'validating' && (
                                <div className="w-4 h-4 loading-spinner"></div>
                              )}
                              {status.status === 'completed' && (
                                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              {status.status === 'failed' && (
                                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>

                            {/* File Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {status.name}
                                </p>
                                <span className="ml-2 text-xs text-gray-500">
                                  {formatFileSize(status.size)}
                                </span>
                              </div>

                              
                              {/* Error Message */}
                              {status.error && (
                                <p className="mt-1 text-xs text-red-600">{status.error}</p>
                              )}
                            </div>

                            {/* Status Label */}
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                status.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                                status.status === 'uploading' ? 'bg-blue-100 text-blue-800' :
                                status.status === 'validating' ? 'bg-yellow-100 text-yellow-800' :
                                status.status === 'completed' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {status.status === 'pending' && 'Pending'}
                                {status.status === 'uploading' && 'Uploading'}
                                {status.status === 'validating' && 'Validating'}
                                {status.status === 'completed' && 'Completed'}
                                {status.status === 'failed' && 'Failed'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* File Selection UI (when not uploading) */}
                {!uploading && !creating && (
                  <div>
                    {/* File Drop Zone */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        dragActive ? 'bg-blue-50 border-blue-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-4">
                        <div className="mx-auto w-12 h-12 text-gray-400">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-lg font-medium text-gray-900">Drop your images here</p>
                          <p className="text-sm text-gray-500">or click to browse</p>
                        </div>
                        <div className="flex justify-center">
                          <label className="cursor-pointer btn-primary">
                            <input
                              type="file"
                              multiple
                              accept="image/*,.eps,.ai,.pdf,.zip,application/postscript,application/illustrator,application/pdf"
                              onChange={handleFileSelect}
                              className="hidden"
                            />
                            Select Files
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">
                          Supports: JPG, PNG, GIF, WebP, BMP, TIF, SVG, AVIF, HEIC, EPS, AI, PDF, ZIP files<br />
                          Max size: 100MB per file
                        </p>
                      </div>
                    </div>

                    {/* Selected Files List */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-6">
                        <h4 className="mb-3 text-sm font-medium text-gray-700">
                          Selected Files ({selectedFiles.length})
                        </h4>
                        <div className="overflow-y-auto space-y-2 max-h-60">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <div className="flex flex-1 items-center space-x-3">
                                <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded">
                                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="p-1 text-red-600 hover:text-red-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-6">
              <div>
                {step === 2 && (
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn-outline"
                    disabled={creating}
                  >
                    Back
                  </button>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-outline"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={creating || (step === 2 && selectedFiles.length === 0)}
                >
                  {creating ? (
                    <span className="flex items-center">
                      <div className="mr-2 w-4 h-4 loading-spinner"></div>
                      {uploading ? 'Uploading...' : 'Creating...'}
                    </span>
                  ) : (
                    step === 1 ? 'Next' : 'Create & Process Batch'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BatchesPage;